import { useLeftNavigationTheme } from '@ben/orion/hooks/use-left-navigation-theme';
import type { ITheme } from '@ben/orion/utils/theme';
import type { NavLinkProps as MantineNavLinkProps } from '@mantine/core';
import {
  createPolymorphicComponent,
  NavLink as MantineNavLink,
} from '@mantine/core';
import { isArray, isNil } from 'lodash';
import type { ElementType } from 'react';
import { forwardRef, useMemo } from 'react';
import type { LinkProps } from 'react-router-dom';
import { NavLink as ReactRouterNavLink, useLocation } from 'react-router-dom';
import type { CSSObject } from 'styled-components';
import styled, { css } from 'styled-components';
import type { BgColorVariant } from '../types';
import RightSection from './right-section';

interface NavLinkProps
  extends
    Omit<MantineNavLinkProps, 'onClick' | 'onKeyDown'>,
    Omit<LinkProps, 'color' | 'onChange' | 'style' | 'to'> {
  to?: LinkProps['to'];
  component?: ElementType;
  activePathSegment?: string | string[];
  notificationCount?: number;
  ariaLabel?: string;
}

const InnerNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    {
      children,
      component,
      activePathSegment,
      notificationCount,
      ariaLabel,
      ...rest
    },
    ref
  ) => {
    const { bgColorVariant } = useLeftNavigationTheme();
    const withChildren = !!children;

    const { pathname } = useLocation();

    const active = useMemo(() => {
      if (isNil(activePathSegment)) return false;
      if (isArray(activePathSegment)) {
        return activePathSegment.some((segment) =>
          pathname.includes(`/${segment}`)
        );
      }
      return pathname.includes(`/${activePathSegment}`);
    }, [pathname, activePathSegment]);

    return (
      <li>
        <StyledMantineNavLink
          aria-label={ariaLabel}
          renderRoot={
            component
              ? undefined
              : (props: LinkProps) => <ReactRouterNavLink {...props} />
          }
          $bgColorVariant={bgColorVariant}
          forwardedAs={undefined}
          ref={ref}
          $withChildren={withChildren}
          rightSection={
            <RightSection
              withChildren={withChildren}
              notificationCount={notificationCount}
            />
          }
          childrenOffset={16}
          component={component}
          active={active}
          defaultOpened={active}
          {...rest}
        >
          {children}
        </StyledMantineNavLink>
      </li>
    );
  }
);

InnerNavLink.displayName = 'NavLink';

const NavLink = createPolymorphicComponent<'a', NavLinkProps>(InnerNavLink);

interface VariantStylesProps {
  theme: ITheme;
  $withChildren: boolean;
}

const variantStyles = {
  LIGHT: ({ theme, $withChildren }: VariantStylesProps) => css`
    background-color: transparent;
    color: ${theme.palette.neutral[90]};
    .mantine-NavLink-description {
      color: ${theme.palette.neutral[90]};
    }

    .mantine-NavLink-children & {
      .mantine-NavLink-label {
        color: ${theme.palette.neutral[100]};
      }
    }
    ${!$withChildren &&
    css`
      color: ${theme.palette.neutral[90]};
      ::before {
        background-color: ${theme.palette.black(0.2)};
      }
      &.active::before {
        background-color: ${theme.palette.neutral[100]};
      }
      &:hover::before {
        background-color: ${theme.palette.neutral[100]};
      }
      ::after {
        background-color: ${theme.palette.black(0.2)};
      }
    `}
    &:hover, &.active, &[data-active] {
      background-color: ${theme.palette.neutral['00']};
      color: ${theme.palette.neutral[100]};
    }
  `,
  DARK: ({ theme, $withChildren }: VariantStylesProps) => css`
    background-color: transparent;
    color: ${theme.palette.neutral['00']};
    .mantine-NavLink-description {
      color: inherit;
    }
    ${!$withChildren &&
    css`
      ::before {
        background-color: ${theme.palette.white(0.2)};
      }
      &.active::before {
        background-color: ${theme.palette.neutral[40]};
      }
      &:hover::before {
        background-color: ${theme.palette.neutral[40]};
      }
      ::after {
        background-color: ${theme.palette.white(0.2)};
      }
    `}
    &:hover, &.active, &[data-active] {
      background-color: ${theme.palette.white(0.15)};
    }
  `,
};

const StyledMantineNavLink = styled(MantineNavLink)`
  ${({
    theme,
    $bgColorVariant,
    $withChildren,
  }: {
    theme: ITheme;
    $bgColorVariant: BgColorVariant;
    $withChildren: boolean;
  }) => {
    return css`
      border-radius: 3px;
      ${getTypographyWithoutMargin(theme.typography.bodyMd2)}
      .mantine-NavLink-description {
        ${getTypographyWithoutMargin(theme.typography.bodySm1)}
      }
      .mantine-NavLink-section:where([data-rotate]) {
        transform: rotate(180deg);
      }
      padding: ${theme.spacing(1)};
      &.active,
      &[data-active] {
        ${getTypographyWithoutMargin(theme.typography.bodyMd3)}
      }
      ${!$withChildren &&
      css`
        .mantine-NavLink-children & {
          .mantine-NavLink-label {
            ${getTypographyWithoutMargin(theme.typography.bodyMd1)}
          }
          .mantine-NavLink-body {
            display: flex;
            flex-direction: column;
          }
          .mantine-NavLink-description:not(:empty) {
            margin-top: ${theme.spacing(1)};
          }
          &:hover,
          &.active {
            background-color: inherit;
            .mantine-NavLink-description {
              ${getTypographyWithoutMargin(theme.typography.bodySm2)}
            }
          }
          // the before and after elements are for decoration - they add a line to the left of the nav link
          ::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 2px;
          }
          ::after {
            content: '';
            position: absolute;
            bottom: -16px;
            left: 0;
            width: 2px;
            height: ${theme.spacing(2)};
          }
          padding: ${theme.spacing(0, 0, 0, 1.5)};
          margin-bottom: ${theme.spacing(2)};
        }
      `}

      position: relative;
      ${variantStyles[$bgColorVariant]({ theme, $withChildren })};
    `;
  }}
`;

export default NavLink;

const getTypographyWithoutMargin = (typographyVariant: CSSObject) => {
  const { margin, ...typographyWithoutMargin } = typographyVariant;
  return typographyWithoutMargin;
};
